import type {
  MarketingAnnouncement as ContractAnnouncement,
  MarketingAnnouncementHistoryItem as ContractHistoryItem,
  MarketingAnnouncementHistoryPage as ContractHistoryPage,
  MarketingAnnouncementHistorySnapshot as ContractHistorySnapshot,
  MarketingAnnouncementCategory,
  MarketingAnnouncementLine,
  MarketingAnnouncementReplaceRequest,
} from '../api/active/generated/settings';
import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';

export type AnnouncementHeadline = MarketingAnnouncementLine;
export type AnnouncementCategory = MarketingAnnouncementCategory;
export type MarketingAnnouncement = Omit<
  ContractAnnouncement,
  'headlineLines' | 'categories'
> & {
  headlineLines: [AnnouncementHeadline, AnnouncementHeadline];
  categories: AnnouncementCategory[];
  /** Labels projected by the server under the current equipment field policy. */
  deviceLabels?: Record<string, { name?: string; sku?: string }>;
};
export type MarketingAnnouncementWrite = Omit<
  MarketingAnnouncementReplaceRequest,
  'categories'
> & {
  categories: (AnnouncementCategory & { previousName?: string })[];
};

export type MarketingAnnouncementHistoryItem = Omit<
  ContractHistoryItem,
  'headlineLines'
> & {
  headlineLines: [AnnouncementHeadline, AnnouncementHeadline];
};
export type MarketingAnnouncementHistoryPage = Omit<
  ContractHistoryPage,
  'items'
> & {
  items: MarketingAnnouncementHistoryItem[];
};
export type MarketingAnnouncementHistorySnapshot = MarketingAnnouncement &
  Pick<ContractHistorySnapshot, 'savedAt' | 'savedByName'>;

export class MarketingAnnouncementApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function parseAnnouncement(value: unknown): MarketingAnnouncement {
  if (
    !isRecord(value) ||
    typeof value.resourceVersion !== 'string' ||
    !Array.isArray(value.headlineLines) ||
    value.headlineLines.length !== 2 ||
    !value.headlineLines.every(
      (line) =>
        isRecord(line) &&
        typeof line.text === 'string' &&
        typeof line.color === 'string',
    ) ||
    !Array.isArray(value.categories) ||
    !value.categories.every(
      (category) =>
        isRecord(category) &&
        typeof category.name === 'string' &&
        typeof category.enabled === 'boolean' &&
        Array.isArray(category.deviceIds) &&
        category.deviceIds.every((id: unknown) => typeof id === 'string'),
    ) ||
    (value.deviceLabels !== undefined &&
      (!isRecord(value.deviceLabels) ||
        !Object.values(value.deviceLabels).every(
          (label) =>
            isRecord(label) &&
            (label.name === undefined || typeof label.name === 'string') &&
            (label.sku === undefined || typeof label.sku === 'string'),
        )))
  )
    throw new Error('营销说明响应格式不正确，请稍后重试');
  return value as MarketingAnnouncement;
}

async function responseError(response: Response) {
  const problem = (await response.json().catch(() => ({}))) as {
    detail?: string;
    title?: string;
  };
  return new MarketingAnnouncementApiError(
    response.status,
    problem.detail ??
      problem.title ??
      `营销说明请求失败（HTTP ${response.status}）。`,
  );
}

async function readResponse(
  response: Response,
): Promise<MarketingAnnouncement> {
  if (response.ok) return parseAnnouncement(await response.json());
  throw await responseError(response);
}

export function createMarketingAnnouncementApi(
  transport: ActiveTransport,
  csrfToken: string | null,
) {
  return {
    get: (signal?: AbortSignal) =>
      transport
        .requestResponse('/marketing-announcement', {
          cache: 'no-store',
          signal,
        })
        .then(readResponse),
    listHistory: (page = 1, pageSize = 20, signal?: AbortSignal) =>
      transport
        .requestResponse(
          `/marketing-announcement/history?page=${page}&pageSize=${pageSize}`,
          { cache: 'no-store', signal },
        )
        .then(async (response) => {
          if (!response.ok) throw await responseError(response);
          const value: unknown = await response.json();
          if (
            !isRecord(value) ||
            !Array.isArray(value.items) ||
            !value.items.every(
              (item) =>
                isRecord(item) &&
                typeof item.resourceVersion === 'string' &&
                typeof item.savedAt === 'string' &&
                (typeof item.savedByName === 'string' ||
                  item.savedByName === null) &&
                Array.isArray(item.headlineLines) &&
                item.headlineLines.length === 2 &&
                item.headlineLines.every(
                  (line) =>
                    isRecord(line) &&
                    typeof line.text === 'string' &&
                    typeof line.color === 'string',
                ),
            ) ||
            !Number.isInteger(value.total) ||
            !Number.isInteger(value.page) ||
            !Number.isInteger(value.pageSize)
          )
            throw new Error('营销说明历史响应格式不正确，请稍后重试');
          return value as MarketingAnnouncementHistoryPage;
        }),
    getHistory: (resourceVersion: string, signal?: AbortSignal) =>
      transport
        .requestResponse(
          `/marketing-announcement/history/${encodeURIComponent(resourceVersion)}`,
          { cache: 'no-store', signal },
        )
        .then(async (response) => {
          if (!response.ok) throw await responseError(response);
          const value: unknown = await response.json();
          if (
            !isRecord(value) ||
            typeof value.savedAt !== 'string' ||
            (typeof value.savedByName !== 'string' &&
              value.savedByName !== null)
          )
            throw new Error('营销说明历史详情格式不正确，请稍后重试');
          const { savedAt, savedByName, ...announcement } = value;
          return {
            ...parseAnnouncement(announcement),
            savedAt,
            savedByName,
          } as MarketingAnnouncementHistorySnapshot;
        }),
    replace: (
      body: MarketingAnnouncementWrite,
      resourceVersion: string,
      signal?: AbortSignal,
    ) =>
      transport
        .requestResponse('/marketing-announcement', {
          method: 'PUT',
          cache: 'no-store',
          signal,
          headers: {
            'Content-Type': 'application/json',
            'If-Match': `"${resourceVersion}"`,
            'Idempotency-Key': randomUuid(),
            ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
          },
          body: JSON.stringify(body),
        })
        .then(readResponse),
  };
}

export function hasAnnouncementContent(value: MarketingAnnouncement): boolean {
  return (
    value.headlineLines.some((line) => line.text.trim().length > 0) ||
    value.categories.some((category) => category.enabled)
  );
}
